import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { startOfDay, endOfDay, subDays, subMonths, format, parseISO, startOfWeek } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { dealService } from '../../../services/dealService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PipelineChart = ({ deals = [] }) => {
  const { profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredDeals, setFilteredDeals] = useState([]);
  
  // Filter states
  const [dateRange, setDateRange] = useState('last-30-days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedStages, setSelectedStages] = useState(['won']);
  const [selectedUser, setSelectedUser] = useState('all');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [teamMembers, setTeamMembers] = useState([]);

  // Date range options
  const dateRangeOptions = [
    { value: 'last-7-days', label: 'Last 7 days' },
    { value: 'last-30-days', label: 'Last 30 days' },
    { value: 'last-90-days', label: 'Last 90 days' },
    { value: 'last-6-months', label: 'Last 6 months' },
    { value: 'last-year', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  // Stage options
  const stageOptions = [
    { value: 'new', label: 'New' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' }
  ];

  // Time period options
  const timePeriodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  // Load team members for user filter
  useEffect(() => {
    loadTeamMembers();
  }, [profile?.tenant_id]);

  // Load filtered deals when filters change
  useEffect(() => {
    loadFilteredDeals();
  }, [dateRange, customStartDate, customEndDate, selectedStages, selectedUser]);

  const loadTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, first_name, last_name')
        ?.eq('tenant_id', profile?.tenant_id);

      if (error) throw error;

      const members = data?.map(member => ({
        value: member?.id,
        label: `${member?.first_name} ${member?.last_name}`
      })) || [];

      setTeamMembers([{ value: 'all', label: 'All users' }, ...members]);
    } catch (error) {
      console.error('Error loading team members:', error);
      setTeamMembers([{ value: 'all', label: 'All users' }]);
    }
  };

  const loadFilteredDeals = async () => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = getDateRangeValues();
      
      const filters = {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        stages: selectedStages,
        assignedTo: selectedUser
      };

      const data = await dealService?.getFilteredDeals(filters);
      setFilteredDeals(data || []);
    } catch (error) {
      console.error('Error loading filtered deals:', error);
      setFilteredDeals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRangeValues = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'last-7-days':
        startDate = startOfDay(subDays(now, 7));
        endDate = endOfDay(now);
        break;
      case 'last-30-days':
        startDate = startOfDay(subDays(now, 30));
        endDate = endOfDay(now);
        break;
      case 'last-90-days':
        startDate = startOfDay(subDays(now, 90));
        endDate = endOfDay(now);
        break;
      case 'last-6-months':
        startDate = startOfDay(subMonths(now, 6));
        endDate = endOfDay(now);
        break;
      case 'last-year':
        startDate = startOfDay(subMonths(now, 12));
        endDate = endOfDay(now);
        break;
      case 'custom':
        startDate = customStartDate ? startOfDay(parseISO(customStartDate)) : startOfDay(subDays(now, 30));
        endDate = customEndDate ? endOfDay(parseISO(customEndDate)) : endOfDay(now);
        break;
      default:
        startDate = startOfDay(subDays(now, 30));
        endDate = endOfDay(now);
    }

    return { startDate, endDate };
  };

  // Calculate chart data from filtered deals
  const chartData = useMemo(() => {
    if (!filteredDeals || filteredDeals?.length === 0) return [];

    const dataMap = {};

    filteredDeals?.forEach(deal => {
      if (!deal?.createdAt) return;
      
      const dealDate = parseISO(deal?.createdAt);
      let periodKey;
      let periodLabel;

      switch (timePeriod) {
        case 'daily':
          periodKey = format(dealDate, 'yyyy-MM-dd');
          periodLabel = format(dealDate, 'MMM dd');
          break;
        case 'weekly':
          const weekStart = startOfWeek(dealDate);
          periodKey = format(weekStart, 'yyyy-MM-dd');
          periodLabel = format(weekStart, 'MMM dd');
          break;
        case 'monthly':
          periodKey = format(dealDate, 'yyyy-MM');
          periodLabel = format(dealDate, 'MMM yyyy');
          break;
        case 'quarterly':
          const quarter = Math.floor(dealDate?.getMonth() / 3) + 1;
          periodKey = `${dealDate?.getFullYear()}-Q${quarter}`;
          periodLabel = `Q${quarter} ${dealDate?.getFullYear()}`;
          break;
        default:
          periodKey = format(dealDate, 'yyyy-MM');
          periodLabel = format(dealDate, 'MMM yyyy');
      }

      if (!dataMap?.[periodKey]) {
        dataMap[periodKey] = { period: periodLabel, deals: 0, revenue: 0 };
      }

      dataMap[periodKey].deals += 1;
      if (deal?.value) {
        dataMap[periodKey].revenue += parseFloat(deal?.value) || 0;
      }
    });

    return Object.values(dataMap)?.sort((a, b) => {
      const aKey = Object.keys(dataMap)?.find(k => dataMap?.[k] === a);
      const bKey = Object.keys(dataMap)?.find(k => dataMap?.[k] === b);
      return aKey?.localeCompare(bKey);
    });
  }, [filteredDeals, timePeriod]);

  const handleStageToggle = (stage) => {
    setSelectedStages(prev => {
      if (prev?.includes(stage)) {
        return prev?.filter(s => s !== stage);
      } else {
        return [...prev, stage];
      }
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-sm text-popover-foreground">
                {payload?.[0]?.value} deals closed
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent rounded-full"></div>
              <span className="text-sm text-popover-foreground">
                ${payload?.[0]?.payload?.revenue?.toLocaleString()} revenue
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div 
      className="bg-card border border-border rounded-xl p-6 shadow-elevation-1"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Pipeline Performance</h3>
            <p className="text-sm text-muted-foreground">Monthly deals closed and revenue generated</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadFilteredDeals}
            loading={isLoading}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range Filter */}
          <Select
            label="Date Range"
            options={dateRangeOptions}
            value={dateRange}
            onChange={setDateRange}
          />

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <>
              <Input
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e?.target?.value)}
              />
              <Input
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e?.target?.value)}
              />
            </>
          )}

          {/* Time Period Filter */}
          <Select
            label="Time Period"
            options={timePeriodOptions}
            value={timePeriod}
            onChange={setTimePeriod}
          />

          {/* User Filter */}
          <Select
            label="Assigned User"
            options={teamMembers}
            value={selectedUser}
            onChange={setSelectedUser}
          />
        </div>

        {/* Stage Filter Chips */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-card-foreground mb-2">Deal Stages</label>
          <div className="flex flex-wrap gap-2">
            {stageOptions?.map(stage => (
              <button
                key={stage?.value}
                onClick={() => handleStageToggle(stage?.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  selectedStages?.includes(stage?.value)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {stage?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="h-80" aria-label="Pipeline Performance Chart">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="mx-auto mb-2 opacity-50 animate-spin" />
              <p>Loading data...</p>
            </div>
          </div>
        ) : chartData?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="period" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="deals" 
                fill="var(--color-primary)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Icon name="BarChart" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No deals data for selected filters</p>
            </div>
          </div>
        )}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-primary rounded-full"></div>
          <span className="text-sm text-muted-foreground">Deals Closed</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {filteredDeals?.length} total deals
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default PipelineChart;